<template>
    <div class="blog-page">
        <!-- Header -->
        <section class="page-header">
            <div class="container">
                <nav class="breadcrumbs">
                    <router-link to="/">Inicio</router-link>
                    <span class="separator">/</span>
                    <span class="current">Blog</span>
                </nav>
                <h1 class="page-title">Blog</h1>
                <p class="page-subtitle">
                    Últimas noticias, tips y novedades del mundo de la tecnología POS
                </p>
            </div>
        </section>
        
        <div class="container">
            <!-- Search -->
            <div class="search-section">
                <div class="search-box">
                    <i class="fas fa-search"></i>
                    <input 
                        type="text" 
                        v-model="searchQuery"
                        @input="filterBlogs"
                        placeholder="Buscar artículos..."
                    >
                </div>
            </div>
            
            <!-- Blog Grid -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <p>Cargando artículos...</p>
            </div>
            
            <div v-else-if="filteredBlogs.length === 0" class="empty-state">
                <i class="fas fa-file-alt"></i>
                <h3>No se encontraron artículos</h3>
                <p>Intenta con otros términos de búsqueda</p>
            </div>
            
            <div v-else class="blog-grid">
                <article 
                    v-for="blog in paginatedBlogs" 
                    :key="blog.id"
                    class="blog-card"
                    @click="goToBlog(blog)"
                >
                    <div class="blog-image">
                        <img :src="getBlogImage(blog)" :alt="blog.titulo">
                        <div class="blog-overlay">
                            <i class="fas fa-arrow-right"></i>
                        </div>
                    </div>
                    
                    <div class="blog-content">
                        <div class="blog-meta">
                            <span v-if="blog.created_at" class="meta-badge date">
                                <i class="fas fa-calendar"></i>
                                {{ formatDate(blog.created_at) }}
                            </span>
                        </div>
                        
                        <h3 class="blog-title">{{ blog.titulo }}</h3>
                        
                        <p v-if="blog.descripcion" class="blog-excerpt">
                            {{ blog.descripcion }}
                        </p>
                        
                        <div class="blog-footer">
                            <span class="read-more">
                                Leer más <i class="fas fa-arrow-right"></i>
                            </span>
                        </div>
                    </div>
                </article>
            </div>
            
            <!-- Pagination -->
            <div class="pagination" v-if="totalPages > 1">
                <button 
                    class="page-btn"
                    :disabled="currentPage === 1"
                    @click="changePage(currentPage - 1)"
                >
                    <i class="fas fa-chevron-left"></i>
                </button>
                
                <button 
                    v-for="page in displayPages" 
                    :key="page"
                    :class="['page-btn', { active: currentPage === page }]"
                    @click="changePage(page)"
                >
                    {{ page }}
                </button>
                
                <button 
                    class="page-btn"
                    :disabled="currentPage === totalPages"
                    @click="changePage(currentPage + 1)"
                >
                    <i class="fas fa-chevron-right"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../services/api';

export default {
    name: 'Blog',
    data() {
        return {
            blogs: [],
            filteredBlogs: [],
            searchQuery: '',
            loading: true,
            currentPage: 1,
            perPage: 9
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.filteredBlogs.length / this.perPage);
        },
        paginatedBlogs() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredBlogs.slice(start, end);
        },
        displayPages() {
            const pages = [];
            const maxPages = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
            let end = Math.min(this.totalPages, start + maxPages - 1);
            
            if (end - start < maxPages - 1) {
                start = Math.max(1, end - maxPages + 1);
            }
            
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            
            return pages;
        }
    },
    async created() {
        await this.loadBlogs();
    },
    methods: {
        async loadBlogs() {
            try {
                this.loading = true;
                const response = await api.getBlogs();
                console.log('Respuesta completa de API:', response);
                console.log('response.data:', response.data);
                
                // La API devuelve paginación de Laravel: response.data.data
                let blogsData = [];
                
                if (response.data) {
                    // Paginación de Laravel
                    if (response.data.data && Array.isArray(response.data.data)) {
                        blogsData = response.data.data;
                        console.log('✅ Datos encontrados en response.data.data');
                    }
                    // Array directo
                    else if (Array.isArray(response.data)) {
                        blogsData = response.data;
                        console.log('✅ Datos encontrados en response.data (array directo)');
                    }
                }
                
                console.log('Blogs procesados:', blogsData);
                console.log('Total de blogs:', blogsData.length);
                
                // IMPORTANTE: Asegurar que siempre sean arrays
                this.blogs = Array.isArray(blogsData) ? blogsData : [];
                this.filteredBlogs = Array.isArray(blogsData) ? blogsData : [];
                
                console.log('this.blogs:', this.blogs);
                console.log('this.filteredBlogs:', this.filteredBlogs);
            } catch (error) {
                console.error('❌ Error loading blogs:', error);
                console.error('Error details:', error.response);
                this.blogs = [];
                this.filteredBlogs = [];
            } finally {
                this.loading = false;
            }
        },
        filterBlogs() {
            // Asegurar que blogs es un array
            const blogsArray = Array.isArray(this.blogs) ? this.blogs : [];
            
            if (!this.searchQuery) {
                this.filteredBlogs = blogsArray;
            } else {
                const query = this.searchQuery.toLowerCase();
                this.filteredBlogs = blogsArray.filter(blog => 
                    (blog.titulo && blog.titulo.toLowerCase().includes(query)) ||
                    (blog.descripcion && blog.descripcion.toLowerCase().includes(query))
                );
            }
            
            console.log('Blogs filtrados:', this.filteredBlogs.length);
            this.currentPage = 1;
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        getBlogImage(blog) {
            if (blog.imagen) {
                return `/storage/${blog.imagen}`;
            }
            return 'https://via.placeholder.com/600x400/1e3a5f/d4af37?text=Blog';
        },
        formatDate(date) {
            if (!date) return '';
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(date).toLocaleDateString('es-ES', options);
        },
        goToBlog(blog) {
            this.$router.push({ 
                name: 'blog.detalle', 
                params: { slug: blog.slug }
            });
        }
    }
};
</script>

<style scoped>
/* VARIABLES */
:root {
    --primary-dark: #0a0e27;
    --secondary-dark: #1a1f3a;
    --accent-gold: #d4af37;
    --accent-teal: #2dd4bf;
    --text-light: #e2e8f0;
    --text-gray: #94a3b8;
    --card-bg: rgba(26, 31, 58, 0.6);
}

.blog-page {
    min-height: 100vh;
    background: linear-gradient(135deg, var(--primary-dark) 0%, var(--secondary-dark) 100%);
    color: var(--text-light);
}

/* PAGE HEADER */
.page-header {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(45, 212, 191, 0.05) 100%);
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
    padding: 3rem 0 2rem;
    margin-bottom: 3rem;
}

.breadcrumbs {
    font-size: 0.9rem;
    color: var(--text-gray);
    margin-bottom: 1rem;
}

.breadcrumbs a {
    color: var(--accent-gold);
    text-decoration: none;
    transition: color 0.3s ease;
}

.breadcrumbs a:hover {
    color: var(--accent-teal);
}

.separator {
    margin: 0 0.5rem;
}

.current {
    color: var(--text-light);
}

.page-title {
    font-family: 'Playfair Display', serif;
    font-size: 3rem;
    color: var(--text-light);
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.page-subtitle {
    font-size: 1.125rem;
    color: var(--text-gray);
    max-width: 600px;
}

/* CONTAINER */
.container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* SEARCH */
.search-section {
    margin-bottom: 3rem;
}

.search-box {
    position: relative;
    max-width: 600px;
    margin: 0 auto;
}

.search-box i {
    position: absolute;
    left: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--accent-gold);
}

.search-box input {
    width: 100%;
    padding: 1rem 1rem 1rem 3.5rem;
    background: var(--card-bg);
    border: 2px solid rgba(212, 175, 55, 0.2);
    border-radius: 50px;
    color: var(--text-light);
    font-size: 1rem;
    transition: all 0.3s ease;
}

.search-box input:focus {
    outline: none;
    border-color: var(--accent-gold);
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.1);
}

.search-box input::placeholder {
    color: var(--text-gray);
}

/* BLOG GRID */
.blog-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;
}

.blog-card {
    background: var(--card-bg);
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid rgba(212, 175, 55, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.blog-card:hover {
    transform: translateY(-10px);
    border-color: var(--accent-gold);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.2);
}

.blog-image {
    position: relative;
    width: 100%;
    height: 250px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.05);
}

.blog-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.blog-card:hover .blog-image img {
    transform: scale(1.1);
}

.blog-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 14, 39, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.blog-card:hover .blog-overlay {
    opacity: 1;
}

.blog-overlay i {
    font-size: 2.5rem;
    color: var(--accent-gold);
}

.blog-content {
    padding: 2rem;
}

.blog-meta {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.meta-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
}

.meta-badge.date {
    background: rgba(212, 175, 55, 0.1);
    color: var(--accent-gold);
    border: 1px solid rgba(212, 175, 55, 0.3);
}

.meta-badge.category {
    background: rgba(45, 212, 191, 0.1);
    color: var(--accent-teal);
    border: 1px solid rgba(45, 212, 191, 0.3);
}

.blog-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.5rem;
    color: var(--text-light);
    margin-bottom: 1rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.blog-excerpt {
    color: var(--text-gray);
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.blog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1rem;
    border-top: 1px solid rgba(212, 175, 55, 0.1);
}

.blog-author {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-gray);
    font-size: 0.875rem;
}

.blog-author i {
    color: var(--accent-gold);
}

.read-more {
    color: var(--accent-teal);
    font-weight: 600;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.read-more i {
    transition: transform 0.3s ease;
}

.blog-card:hover .read-more i {
    transform: translateX(5px);
}

/* PAGINATION */
.pagination {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin: 3rem 0;
}

.page-btn {
    min-width: 45px;
    height: 45px;
    padding: 0 1rem;
    background: var(--card-bg);
    border: 2px solid rgba(212, 175, 55, 0.2);
    border-radius: 10px;
    color: var(--text-gray);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.page-btn:hover:not(:disabled) {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    transform: translateY(-2px);
}

.page-btn.active {
    background: linear-gradient(135deg, var(--accent-gold), var(--accent-teal));
    border-color: transparent;
    color: var(--primary-dark);
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
}

.page-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

/* LOADING & EMPTY STATES */
.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    text-align: center;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid rgba(212, 175, 55, 0.1);
    border-top-color: var(--accent-gold);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-state p {
    margin-top: 1rem;
    color: var(--text-gray);
}

.empty-state i {
    font-size: 4rem;
    color: rgba(212, 175, 55, 0.3);
    margin-bottom: 1rem;
}

.empty-state h3 {
    font-size: 1.5rem;
    color: var(--text-light);
    margin-bottom: 0.5rem;
}

.empty-state p {
    color: var(--text-gray);
}

/* RESPONSIVE */
@media (max-width: 768px) {
    .page-title {
        font-size: 2rem;
    }
    
    .container {
        padding: 0 1rem;
    }
    
    .blog-grid {
        grid-template-columns: 1fr;
    }
}
</style>